import { useState } from "react";
import { C } from "../constants/colors";

export function UsageChart({ dailyStats, allTimeCost, allTimeTokens, truncated = 0 }) {
  const [mode, setMode] = useState("cost");   // "cost" | "tokens"
  const [period, setPeriod] = useState(30);   // 7 | 30 | 365

  // For year view, aggregate daily → monthly
  const aggregateByMonth = (days) => {
    const m = {};
    days.forEach(d => {
      const key = d.date.slice(0, 7);
      if (!m[key]) m[key] = { date: key, tokens: 0, inputTokens: 0, outputTokens: 0, cost: 0 };
      m[key].tokens += d.tokens; m[key].inputTokens += d.inputTokens;
      m[key].outputTokens += d.outputTokens; m[key].cost = Math.round((m[key].cost + d.cost) * 10000) / 10000;
    });
    return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
  };

  const rawData = period === 7 ? dailyStats.slice(-7) : period === 30 ? dailyStats.slice(-30) : dailyStats;
  const data = period === 365 ? aggregateByMonth(rawData) : rawData;
  const values = data.map(d => mode === "cost" ? d.cost : d.tokens);
  const maxVal = Math.max(...values, 0.0001);

  const week = dailyStats.slice(-7);
  const month = dailyStats.slice(-30);
  const sum = (arr, key) => arr.reduce((s, d) => s + d[key], 0);

  const fmtCost = v => v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
  const fmtTok = v => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(1)}K` : v;

  const [hovered, setHovered] = useState(null);

  // SVG dimensions
  const VW = 520, VH = 140;
  const PAD = { top: 8, right: 8, bottom: 28, left: 44 };
  const cW = VW - PAD.left - PAD.right;
  const cH = VH - PAD.top - PAD.bottom;
  const n = data.length;
  const barW = Math.max(3, cW / n - (n > 10 ? 2 : 4));
  const slotW = cW / n;

  // Y-axis ticks
  const yTicks = [0, 0.5, 1].map(f => ({ val: maxVal * f, y: PAD.top + cH * (1 - f) }));

  const statCard = (label, cost, tokens) => (
    <div style={{ background: "#f8f7fa", borderRadius: 10, padding: "10px 14px", minWidth: 100, flex: 1 }}>
      <p style={{ margin: "0 0 4px", fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</p>
      <p style={{ margin: "0 0 2px", fontSize: 16, fontWeight: 700, color: C.indigo }}>{fmtCost(cost)}</p>
      <p style={{ margin: 0, fontSize: 11, color: "#888" }}>{fmtTok(tokens)} tokens</p>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Summary cards */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {statCard("Last 7 days", sum(week, "cost"), sum(week, "tokens"))}
        {statCard("Last 30 days", sum(month, "cost"), sum(month, "tokens"))}
        {statCard("All time", allTimeCost, allTimeTokens)}
      </div>

      {/* Only rendered when it has something to say. A permanent "0 truncated"
          card would be noise; a card that appears is the alert. */}
      {truncated > 0 && (
        <div style={{ background: "#fdf2f2", border: "1px solid #f5c6c6", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "#9b2c2c", lineHeight: 1.5 }}>
          <b>{truncated} {truncated === 1 ? "reply was" : "replies were"} cut off at the token ceiling in the last 7 days.</b>{" "}
          Those users saw an answer that stopped mid-sentence. Raise <code>max_tokens</code> for the affected call.
        </div>
      )}

      {/* Chart controls */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ v: "cost", l: "Cost ($)" }, { v: "tokens", l: "Tokens" }].map(({ v, l }) => (
            <button key={v} onClick={() => setMode(v)} style={{ padding: "4px 12px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: mode === v ? C.indigo : "#f0eff6", color: mode === v ? "#fff" : "#555" }}>{l}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ p: 7, l: "7d" }, { p: 30, l: "30d" }, { p: 365, l: "1 year" }].map(({ p, l }) => (
            <button key={p} onClick={() => setPeriod(p)} style={{ padding: "4px 12px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: period === p ? C.indigo : "#f0eff6", color: period === p ? "#fff" : "#555" }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "12px 12px 4px", border: "1px solid #e8e8f0", position: "relative" }}>
        <svg viewBox={`0 0 ${VW} ${VH}`} style={{ width: "100%", display: "block" }}>
          {/* Y-axis gridlines + labels */}
          {yTicks.map(({ val, y }, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={VW - PAD.right} y2={y} stroke="#ebebf2" strokeWidth={1} />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize={9} fill="#bbb">
                {mode === "cost" ? (val === 0 ? "$0" : fmtCost(val)) : fmtTok(Math.round(val))}
              </text>
            </g>
          ))}

          {/* Bars */}
          {data.map((d, i) => {
            const val = values[i];
            const barH = Math.max(val > 0 ? 2 : 0, (val / maxVal) * cH);
            const x = PAD.left + i * slotW + (slotW - barW) / 2;
            const y = PAD.top + cH - barH;
            const isHov = hovered === i;
            return (
              <g key={d.date}>
                <rect x={x} y={y} width={barW} height={barH} fill={isHov ? C.tealLight : C.teal} rx={2} opacity={isHov ? 1 : 0.85}
                  onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: "default" }} />
                {/* X-axis label */}
                {(i === 0 || i === n - 1 || i % Math.max(1, Math.floor(n / 5)) === 0) && (
                  <text x={x + barW / 2} y={VH - PAD.bottom + 12} textAnchor="middle" fontSize={8} fill="#bbb">
                    {period === 365
                      ? new Date(d.date + "-02").toLocaleString("default", { month: "short" })
                      : d.date.slice(5)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hovered !== null && data[hovered] && (
          <div style={{ position: "absolute", top: 8, right: 16, background: C.indigo, color: "#fff", borderRadius: 8, padding: "6px 10px", fontSize: 11, pointerEvents: "none" }}>
            <p style={{ margin: "0 0 2px", fontWeight: 700 }}>{data[hovered].date}</p>
            <p style={{ margin: "0 0 1px" }}>{fmtCost(data[hovered].cost)}</p>
            <p style={{ margin: 0, color: C.tealLight }}>{fmtTok(data[hovered].tokens)} tokens</p>
          </div>
        )}
      </div>
    </div>
  );
}
